import { zodResolver } from "@hookform/resolvers/zod";
import {
    Modal,
    SelectField,
    TextAreaField,
    TextInputField,
    useToasts,
} from "@twinkltech/curriculum-architect-component-library";
import {
    ApiResponse,
    CreateCurriculumPayload,
    CreateCurriculumPayloadSchema,
    CreateCurriculumResponse,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import { Button } from "@twinkltech/web-ui";
import { useId, useState } from "react";
import { SubmitHandler, useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";

import { GENERIC_CURRICULUM_FORM_NS } from "@/components/GenericCurriculumModal/GenericCurriculumModal.i18n";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { useFeedback } from "@/providers/Feedback/Feedback.context";
import { captureAppError } from "@/utils/monitoring/sentry";

import { REQUIRED_FIELD_MESSAGE } from "@/utils/validation-messages";

import { NEW_CURRICULUM_MODAL_NS } from "../../NewCurriculumModal.i18n";

const CurriculumFormSchema = z.object({
  displayLabel: z.string().min(1, REQUIRED_FIELD_MESSAGE).max(120),
  description: z.string().max(500).optional(),
  status: z.enum(["active", "archived"]),
  progress: z.enum(["not_yet_planned", "planned", "planning_in_progress"]),
});

type CurriculumFormData = z.infer<typeof CurriculumFormSchema>;

export const IndividualNewCurriculumModal = ({
  isLoading,
  onHandleSubmit,
  open,
  setOpen,
}: {
  isLoading: boolean;
  open: boolean;
  onHandleSubmit: (params: {
    payload: CreateCurriculumPayload;
  }) => ApiResponse<CreateCurriculumResponse>;
  setOpen: (open: boolean) => void;
}) => {
  const { t } = useTranslation(NEW_CURRICULUM_MODAL_NS);

  const { click } = useTwinklAnalytics();

  const onHandleOnClose = () => {
    setOpen(false);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { incrementEngagementCount } = useFeedback();

  const { addErrorToast, addSuccessToast } = useToasts();

  // Accessibility IDs
  const formId = useId();
  const currNameInputId = useId();
  const currDescriptionInputId = useId();
  const currVisibilityInputId = useId();
  const currPlanningProgressInputId = useId();

  const formDefaultValues: CurriculumFormData = {
    displayLabel: "",
    description: "",
    status: "active",
    progress: "not_yet_planned",
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<CurriculumFormData, unknown, CurriculumFormData>({
    resolver: zodResolver<CurriculumFormData, unknown, CurriculumFormData>(
      CurriculumFormSchema
    ),
    defaultValues: formDefaultValues,
  });

  // Note: Need to use a controller for the Headless UI custom components
  const { field: progressField } = useController({
    control,
    name: "progress",
  });
  const { field: statusField } = useController({
    control,
    name: "status",
  });

  const onCancel = () => {
    click("click_add_new_modal_cta", {
      ctaCopy: "Cancel",
    });
    onHandleOnClose();
  };

  const onSubmit = async (data: CreateCurriculumPayload) => {
    const result = CreateCurriculumPayloadSchema.safeParse(data);

    if (result.success) {
      return await onHandleSubmit({
        payload: result.data,
      });
    }

    console.error("Form data validation failed:", result.error);
    return result.error;
  };

  const onFormSubmit: SubmitHandler<CurriculumFormData> = async (
    data,
    event
  ) => {
    event?.preventDefault();

    if (isLoading || isSubmitting) {
      return;
    }

    incrementEngagementCount();

    const mappedData: CreateCurriculumPayload = {
      displayLabel: data.displayLabel,
      description: data.description,
      status: data.status,
      progress: data.progress,
      // for individuals, private is always true
      private: true,
    };

    setIsSubmitting(true);
    const result = await onSubmit(mappedData);

    if (result instanceof Error) {
      captureAppError(result, {
        action: "create",
        area: "curriculum",
        extra: {
          component: "IndividualNewCurriculumModal",
        },
      });
      addErrorToast({
        message: "Update failed",
      });
    } else {
      addSuccessToast({
        message: "Curriculum created successfully",
      });
      onHandleOnClose();
    }

    click("click_add_new_modal_cta", {
      ctaCopy: "Add New Curriculum",
    });

    setIsSubmitting(false);
  };

  return (
    <Modal hasCloseButton open={open} width="md" onClose={onCancel}>
      <Modal.Header>
        <Modal.Title>{t("title", { ns: NEW_CURRICULUM_MODAL_NS })}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="mb-2 max-h-[80vh]">
        <form
          className="py-2"
          id={formId}
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <div className="space-y-3" role="group">
            <TextInputField
              id={currNameInputId}
              infoText={t("inputs.name.info", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              label={t("inputs.name.label", { ns: GENERIC_CURRICULUM_FORM_NS })}
              maxLength={120}
              placeholder={t("inputs.name.placeholder", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              {...register("displayLabel", { required: true })}
              errorText={errors.displayLabel?.message}
            />
            <TextAreaField
              id={currDescriptionInputId}
              infoText={t("inputs.description.info", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              label={t("inputs.description.label", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              maxLength={500}
              placeholder={t("inputs.description.placeholder", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              {...register("description")}
              errorText={errors.description?.message}
            />
            <SelectField
              id={currVisibilityInputId}
              label={t("inputs.status.label", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              options={[
                { value: "active", label: "Active" },
                { value: "archived", label: "Archived" },
              ]}
              {...statusField}
              defaultValue={formDefaultValues.status}
              errorText={errors.status?.message}
            />
            <SelectField
              id={currPlanningProgressInputId}
              label={t("inputs.progress.label", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              options={[
                { value: "not_yet_planned", label: "Not yet planned" },
                {
                  value: "planning_in_progress",
                  label: "Planning in progress",
                },
                { value: "planned", label: "Planned" },
              ]}
              {...progressField}
              defaultValue={formDefaultValues.progress}
              errorText={errors.progress?.message}
            />
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer className="gap-2">
        <Button type="button" variant="outlined" onClick={onCancel}>
          {t("actions.cancel", { ns: COMMON_NS })}
        </Button>
        <Button
          disabled={isLoading || !open || isSubmitting}
          form={formId}
          variant="primary"
        >
          {t("actions.save", { ns: NEW_CURRICULUM_MODAL_NS })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
